import { useState, useEffect, useRef, useMemo } from 'react';

// In-memory cache: key = restaurantId + dishId, value = translated dish fields
const translationCache = new Map();

// Known MyMemory mistranslations to correct after translation
const TRANSLATION_CORRECTIONS = {
  'lawyer': 'avocado',
  'Lawyer': 'Avocado',
  'LAWYER': 'AVOCADO',
};

// Known dish name corrections (French → correct English)
const DISH_NAME_CORRECTIONS = {
  'Linguine au Homard': 'Linguine with Lobster',
};

function buildCacheKey(restaurantId, dishId) {
  return `${restaurantId ?? 'default'}_${dishId}`;
}

/**
 * Applies known corrections to a translated string.
 */
function applyCorrections(text) {
  if (!text || typeof text !== 'string') return text;
  let result = text;
  for (const [wrong, correct] of Object.entries(TRANSLATION_CORRECTIONS)) {
    // Replace whole-word occurrences only
    result = result?.replace(new RegExp(`\\b${wrong}\\b`, 'g'), correct);
  }
  return result;
}

/**
 * Translates a single text string from French to English using MyMemory API (free, no key required).
 */
async function translateText(text) {
  if (!text || typeof text !== 'string' || text?.trim() === '') return text;
  try {
    const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=fr|en`;
    const res = await fetch(url);
    const data = await res?.json();
    const translated = data?.responseData?.translatedText;
    let result = translated && translated !== text ? translated : text;
    return applyCorrections(result);
  } catch {
    return text;
  }
}

/**
 * Translates a single dish object (name, description, ingredients) from French to English.
 */
async function translateDish(dish) {
  // Check for known dish name corrections first (skip API call for name if known)
  const correctedName = DISH_NAME_CORRECTIONS?.[dish?.name];

  const [name, description] = await Promise.all([
    correctedName ? Promise.resolve(correctedName) : translateText(dish?.name),
    translateText(dish?.description),
  ]);

  let ingredients = dish?.ingredients;
  if (Array.isArray(dish?.ingredients) && dish?.ingredients?.length > 0) {
    ingredients = await Promise.all(dish?.ingredients?.map((ing) => translateText(ing)));
  } else if (typeof dish?.ingredients === 'string' && dish?.ingredients?.trim()) {
    ingredients = await translateText(dish?.ingredients);
  }

  return { name, description, ingredients };
}

/**
 * Translates an array of dishes from French to English using MyMemory (free API).
 * Returns the translated dishes (or original if language is 'fr').
 */
export function useDishTranslation(dishes, language, restaurantId) {
  const [translatedDishes, setTranslatedDishes] = useState(dishes);
  const [isTranslating, setIsTranslating] = useState(false);
  const abortRef = useRef(false);

  // Stable key derived from dish IDs — avoids re-running the effect when the
  // array reference changes but the actual dish list hasn't changed.
  const dishIdsKey = useMemo(
    () => (dishes || [])?.map((d) => d?.id)?.join(','),
    [dishes]
  );

  useEffect(() => {
    if (language !== 'en' || !dishes?.length) {
      setTranslatedDishes(dishes);
      setIsTranslating(false);
      return;
    }

    // Check which dishes still need translation
    const needsTranslation = dishes?.filter(
      (d) => !translationCache?.has(buildCacheKey(restaurantId, d?.id))
    );

    if (needsTranslation?.length === 0) {
      // All cached — apply immediately
      setTranslatedDishes(
        dishes?.map((d) => {
          const cached = translationCache?.get(buildCacheKey(restaurantId, d?.id));
          return cached ? { ...d, ...cached } : d;
        })
      );
      return;
    }

    abortRef.current = false;
    setIsTranslating(true);

    // Apply cached ones immediately while waiting for the rest
    setTranslatedDishes(
      dishes?.map((d) => {
        const cached = translationCache?.get(buildCacheKey(restaurantId, d?.id));
        return cached ? { ...d, ...cached } : d;
      })
    );

    // Translate dishes one by one (MyMemory free tier: sequential to avoid rate limits)
    const runTranslations = async () => {
      for (const dish of needsTranslation) {
        if (abortRef?.current) break;
        try {
          const translated = await translateDish(dish);
          const key = buildCacheKey(restaurantId, dish?.id);
          translationCache?.set(key, translated);

          if (!abortRef?.current) {
            setTranslatedDishes(
              dishes?.map((d) => {
                const cached = translationCache?.get(buildCacheKey(restaurantId, d?.id));
                return cached ? { ...d, ...cached } : d;
              })
            );
          }
        } catch (err) {
          console.warn('useDishTranslation: failed to translate dish', dish?.id, err);
        }
      }

      if (!abortRef?.current) {
        setIsTranslating(false);
      }
    };

    runTranslations()?.catch((err) => {
      console.error('useDishTranslation: translation run failed', err);
      if (!abortRef?.current) {
        setTranslatedDishes(dishes);
        setIsTranslating(false);
      }
    });

    return () => {
      abortRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dishIdsKey, language, restaurantId]);

  return { translatedDishes, isTranslating };
}
